import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { X, User, ArrowRight, Search, CheckCircle, AlertCircle } from "lucide-react";
import { extractApiError } from "../../lib/apiError";

export default function AssignDriverModal({ quote, onClose, onAssigned, allowSkip = true }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(quote?.driver_id || "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/drivers`)
      .then(res => setDrivers(res.data))
      .catch(() => toast.error("Failed to load drivers"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = drivers.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.name?.toLowerCase().includes(q) || d.surname?.toLowerCase().includes(q) || d.email?.toLowerCase().includes(q);
  });

  const isChange = !!quote?.driver_id;

  const submit = async () => {
    if (!selectedId) { toast.error("Please select a driver"); return; }
    if (selectedId === quote.driver_id) { toast.info("That driver is already assigned"); return; }
    setSubmitting(true);
    try {
      const res = await axios.patch(`${API}/admin/quotes/${quote.quote_id}/driver`, { driver_id: selectedId });
      toast.success(res.data?.is_reassignment ? "Driver changed. Customer notified by email." : "Driver assigned. Customer notified by email.");
      onAssigned?.();
    } catch (e) {
      toast.error(extractApiError(e, "Failed to assign"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="assign-driver-modal">
      <div className="bg-[#121212] border border-[#C5A059]/30 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" /> {isChange ? "Change Driver" : "Booking Confirmed - Assign a Driver"}
            </h3>
            <p className="text-white/40 text-xs mt-1">
              {quote.user_name} &middot; {quote.origin} <ArrowRight className="inline w-3 h-3" /> {quote.destination} &middot; {quote.pickup_date} {quote.pickup_time}
            </p>
            {isChange && quote.driver_name && (
              <p className="text-violet-400 text-xs mt-1">Currently assigned: <strong>{quote.driver_name}</strong></p>
            )}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {drivers.length === 0 && !loading && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 flex items-start gap-3 text-amber-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">No drivers available yet</p>
                <p className="text-amber-200/60 text-xs mt-1">Add drivers from the Drivers tab first. The booking is already confirmed; you can assign a driver later.</p>
              </div>
            </div>
          )}

          {drivers.length > 0 && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="input-dark pl-10" data-testid="assign-driver-search" />
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
                {loading ? <div className="flex justify-center py-10"><div className="spinner" /></div> : (
                  filtered.length === 0 ? <p className="text-white/40 text-center py-6">No drivers match your search</p> :
                  filtered.map(d => (
                    <label key={d.driver_id}
                      className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${selectedId === d.driver_id ? "bg-[#C5A059]/10 border-[#C5A059]/40" : "bg-[#151515] border-white/5 hover:border-white/15"}`}
                      data-testid={`driver-option-${d.driver_id}`}>
                      <input type="radio" name="driver" value={d.driver_id} checked={selectedId === d.driver_id}
                        onChange={() => setSelectedId(d.driver_id)} className="accent-[#C5A059]" />
                      <div className="w-9 h-9 rounded-full bg-[#C5A059]/20 flex items-center justify-center"><User className="w-4 h-4 text-[#C5A059]" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{d.name} {d.surname}</p>
                        <p className="text-white/40 text-xs">{d.email} &middot; {d.mobile}</p>
                      </div>
                      <span className="text-white/40 text-xs whitespace-nowrap">{d.transfers_count || 0} transfers</span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            {allowSkip && (
              <Button variant="ghost" onClick={onClose} className="flex-1 text-white/60" data-testid="assign-skip">
                Skip for now
              </Button>
            )}
            <Button onClick={submit} disabled={submitting || !selectedId || drivers.length === 0} className="flex-1 btn-gold" data-testid="assign-confirm">
              {submitting ? (isChange ? "Changing..." : "Assigning...") : (isChange ? "Change & Notify Customer" : "Assign & Notify Customer")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
